import type {
  AuthenticatedUserContract,
  InstrumentQueueItemContract,
  Locale,
} from "@chams-pay/contracts";
import { translate } from "@chams-pay/contracts";
import { Button as ReportButton } from "@chams-pay/design-system/components/button";
import {
  ActionsButton,
  ActionsGroup,
  ActionsLabel,
  Block,
  BlockTitle,
  Button,
  List,
  ListInput,
  ListItem,
  Segmented,
  SegmentedButton,
} from "konsta/react";
import { Download, MoreVertical, Printer } from "lucide-react";
import { useState } from "react";
import { casablancaDate, formatStamp, initialOf } from "../core/format.js";
import { deploymentProductName } from "../core/deployment.js";
import { instrumentTransitionCopy } from "../core/instrument-transition-copy.js";
import { showMobileFeedback } from "./app-feedback.js";
import type {
  InstrumentStatusFilter,
  InstrumentsController,
  ReportsController,
} from "../core/use-operations.js";
import {
  instrumentBucket,
  useAccessCodes,
  useInstallationRequests,
  useSystemStatus,
} from "../core/use-operations.js";
import { A4DocumentPreview } from "../ui/a4-document-preview.js";
import { DatePicker } from "../ui/date-picker.js";
import { Amount, Bidi, Count, DateValue } from "../ui/figures.js";
import { MonthPicker } from "../ui/month-picker.js";
import { ReportDocument } from "../ui/report-document.js";
import { SchoolSettingsPanel } from "../ui/school-settings.js";
import {
  AccessCodeReveal,
  CalendarGlyph,
  CheckCircleGlyph,
  ChequeGlyph,
  ScopedEmpty,
  ScopedError,
  SkeletonLines,
  TransferGlyph,
} from "../ui/states.js";
import { MobileDraggableActions } from "./draggable-sheet.js";
import { TabbarSpacer } from "./shell.js";
import { MobileSurface } from "./surfaces.js";

const filterKey = {
  pending: "ops.pendingShort",
  settled: "ops.settledTab",
  rejected: "ops.rejected",
} as const satisfies Record<InstrumentStatusFilter, Parameters<typeof translate>[1]>;

function StatusPill({
  locale,
  item,
}: {
  readonly locale: Locale;
  readonly item: InstrumentQueueItemContract;
}) {
  const bucket = instrumentBucket(item.status);
  const tone =
    bucket === "pending"
      ? "bg-warning-soft text-warning"
      : bucket === "rejected"
        ? "bg-destructive-soft text-destructive"
        : "bg-success-soft text-success";
  const label =
    bucket === "pending"
      ? translate(locale, "ops.awaitingSettlement")
      : bucket === "rejected"
        ? translate(locale, "ops.rejected")
        : translate(locale, "ops.settled");
  return (
    <span
      className={`inline-flex h-6 items-center gap-1.5 rounded-full px-2.5 font-heading text-[11px] font-bold ${tone}`}
    >
      <span aria-hidden="true" className="size-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

export function MobileInstrumentsScreen({
  locale,
  controller,
  onOpenStudent,
}: {
  readonly locale: Locale;
  readonly controller: InstrumentsController;
  readonly onOpenStudent: (studentId: string) => void;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const [actionsFor, setActionsFor] = useState<string | null>(null);
  const target = controller.items.find((item) => item.paymentId === actionsFor) ?? null;
  const pending = controller.items.filter((item) => item.status === "pending");
  const pendingTotal = pending.reduce((total, item) => total + BigInt(item.amountMad), 0n);
  const filters: readonly InstrumentStatusFilter[] = ["pending", "settled", "rejected"];

  return (
    <section className="operations-screen instruments-screen" data-testid="instruments-screen">
      <Block className="!mt-3 text-xs text-muted-foreground">
        <Count value={pending.length} /> {t("ops.pendingItems")} ·{" "}
        <Amount value={pendingTotal} locale={locale} />
      </Block>

      <Block className="!my-2">
        <Segmented
          strong
          className="mobile-segmented"
          data-tab-count="3"
          data-active-index={String(filters.indexOf(controller.filter))}
        >
          {filters.map((value) => (
            <SegmentedButton
              key={value}
              active={controller.filter === value}
              className="min-h-11"
              onClick={() => controller.setFilter(value)}
            >
              {t(filterKey[value])}
            </SegmentedButton>
          ))}
        </Segmented>
      </Block>

      {controller.loadFailed ? (
        <Block>
          <ScopedError locale={locale} onRetry={() => void controller.reload()} />
        </Block>
      ) : controller.actionFailed && !controller.selected ? (
        <Block>
          <ScopedError
            locale={locale}
            title={t("ops.settleFailed")}
            body={t("ops.settleFailedBody")}
            onRetry={() => void controller.reload()}
          />
        </Block>
      ) : controller.visible.length ? (
        <>
          <List strong inset className="instrument-list">
            {controller.visible.map((item) => (
              <ListItem
                key={item.paymentId}
                className="instrument-row"
                innerClassName={item.status === "pending" ? "relative pe-14" : ""}
                data-status={item.status}
                link
                linkComponent="div"
                chevron={false}
                role="button"
                tabIndex={0}
                onClick={() => onOpenStudent(item.student.id)}
                onKeyDown={(event) => {
                  if (
                    event.target === event.currentTarget &&
                    (event.key === "Enter" || event.key === " ")
                  ) {
                    event.preventDefault();
                    onOpenStudent(item.student.id);
                  }
                }}
                title={locale === "ar" ? item.student.nameAr : item.student.nameFr}
                subtitle={
                  <span className="flex items-center gap-2 text-xs text-muted-2">
                    <Bidi>{item.reference}</Bidi>
                    {item.method.method === "cheque" ? <ChequeGlyph /> : <TransferGlyph />}
                    <DateValue value={item.receivedDate} />
                  </span>
                }
                text={
                  <span
                    className={
                      item.status === "pending"
                        ? "flex min-h-10 items-center gap-2 pt-1"
                        : "flex items-center gap-2 pt-1"
                    }
                  >
                    <StatusPill locale={locale} item={item} />
                    {item.status === "pending" && (
                      <button
                        type="button"
                        aria-label={t("ops.actions")}
                        className="chams-focusable absolute bottom-2 end-2 grid size-10 place-items-center rounded-xl bg-primary-soft text-primary-strong"
                        onClick={(event) => {
                          event.stopPropagation();
                          setActionsFor(item.paymentId);
                        }}
                      >
                        <MoreVertical aria-hidden="true" className="size-4" />
                      </button>
                    )}
                  </span>
                }
                after={
                  <span className={item.status === "pending" ? "contents" : "flex items-start"}>
                    <Amount
                      value={item.amountMad}
                      locale={locale}
                      className={
                        item.status === "pending"
                          ? "absolute end-2 top-3 text-sm font-extrabold"
                          : "text-sm font-extrabold"
                      }
                    />
                  </span>
                }
              />
            ))}
          </List>
          <Block className="!my-2 flex items-center gap-3 text-xs">
            <strong className="ms-auto font-heading">
              {t("ops.queueTotal")} <Amount value={controller.visibleTotalMad} locale={locale} />
            </strong>
          </Block>
        </>
      ) : (
        <Block>
          <ScopedEmpty
            tone="success"
            glyph={<CheckCircleGlyph />}
            title={controller.filter === "settled" ? t("ops.settledEmpty") : t("ops.queueEmpty")}
            body={
              controller.filter === "settled" ? t("ops.settledEmptyBody") : t("ops.queueEmptyBody")
            }
          />
        </Block>
      )}

      <TabbarSpacer />

      <MobileDraggableActions
        opened={target !== null}
        onClose={() => setActionsFor(null)}
        className="mobile-workspace-actions"
        onBackdropClick={() => setActionsFor(null)}
      >
        <ActionsGroup>
          <ActionsLabel>{target?.reference ?? t("ops.chequesTitle")}</ActionsLabel>
          <ActionsButton
            bold
            onClick={() => {
              if (target) controller.begin(target, "settle");
              setActionsFor(null);
            }}
          >
            {t("ops.confirm")}
          </ActionsButton>
          <ActionsButton
            colors={{ textIos: "text-destructive", textMaterial: "text-destructive" }}
            onClick={() => {
              if (target) controller.begin(target, "reject");
              setActionsFor(null);
            }}
          >
            {t("ops.reject")}
          </ActionsButton>
        </ActionsGroup>
      </MobileDraggableActions>

      <MobileSurfaceForm locale={locale} controller={controller} />
    </section>
  );
}

function MobileSurfaceForm({
  locale,
  controller,
}: {
  readonly locale: Locale;
  readonly controller: InstrumentsController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const settling = controller.action === "settle";
  const copy = instrumentTransitionCopy(locale, controller.selected, controller.action);

  return (
    <MobileSurface
      opened={controller.selected !== null}
      title={copy.title}
      subtitle={copy.subtitle}
      closeLabel={t("common.close")}
      testId="instrument-transition-surface"
      onClose={controller.dismiss}
    >
      <form
        className="operations-form flex flex-1 flex-col"
        onSubmit={(event) => {
          event.preventDefault();
          void controller.submit();
        }}
      >
        <Block strong inset className="instrument-transition-fields flex flex-col gap-4 !bg-white">
          <DatePicker
            locale={locale}
            label={copy.dateLabel}
            showLabel
            max={casablancaDate()}
            value={controller.statementDate}
            onChange={controller.setStatementDate}
          />
          {settling ? (
            <label className="flex flex-col gap-1.5">
              <span className="font-heading text-[11px] font-semibold text-muted-foreground">
                {copy.fieldLabel} · {t("ops.optional")}
              </span>
              <input
                type="text"
                maxLength={120}
                value={controller.statementReference}
                className="chams-figure min-h-12 w-full rounded-2xl border border-input bg-white px-3.5 text-sm text-foreground outline-none"
                onChange={(event) => controller.setStatementReference(event.target.value)}
              />
            </label>
          ) : (
            <label className="flex flex-col gap-1.5">
              <span className="font-heading text-[11px] font-semibold text-muted-foreground">
                {copy.fieldLabel}
              </span>
              <textarea
                required
                minLength={3}
                maxLength={500}
                value={controller.reason}
                className="min-h-28 w-full resize-y rounded-2xl border border-input bg-white px-3.5 py-2.5 text-sm text-foreground outline-none"
                onChange={(event) => controller.setReason(event.target.value)}
              />
            </label>
          )}
        </Block>

        {settling && (
          <Block
            strong
            inset
            className="!bg-success-soft text-[11.5px] leading-relaxed text-success"
          >
            {copy.note}
          </Block>
        )}

        {controller.actionFailed && (
          <Block>
            <ScopedError
              locale={locale}
              title={copy.failureTitle}
              body={t("ops.settleFailedBody")}
              onRetry={() => void controller.submit()}
            />
          </Block>
        )}

        <div className="mobile-drawer-footer operations-surface-actions gap-2">
          <Button
            component="button"
            type="submit"
            large
            rounded
            className={`min-h-12 flex-1 font-heading font-bold !text-white disabled:!bg-disabled ${
              settling ? "!bg-primary" : "!bg-destructive"
            }`}
            disabled={controller.submitDisabled}
          >
            {copy.title}
          </Button>
          <Button
            component="button"
            type="button"
            large
            rounded
            clear
            className="min-h-12 flex-1"
            onClick={controller.dismiss}
          >
            {t("ops.cancel")}
          </Button>
        </div>
      </form>
    </MobileSurface>
  );
}

/* ------------------------------------------------------------------ */

export function MobileReportsScreen({
  locale,
  controller,
}: {
  readonly locale: Locale;
  readonly controller: ReportsController;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const empty = controller.report !== null && controller.report.activity.length === 0;

  return (
    <section className="operations-screen reports-screen" data-testid="reports-screen">
      <div className="reports-workspace">
        <div className="report-controls no-print">
          <Segmented
            strong
            className="mobile-segmented report-mode-tabs"
            data-tab-count="2"
            data-active-index={controller.mode === "daily" ? "0" : "1"}
          >
            <SegmentedButton
              active={controller.mode === "daily"}
              className="min-h-11"
              onClick={() => controller.setMode("daily")}
            >
              {t("ops.daily")}
            </SegmentedButton>
            <SegmentedButton
              active={controller.mode === "monthly"}
              className="min-h-11"
              onClick={() => controller.setMode("monthly")}
            >
              {t("ops.monthly")}
            </SegmentedButton>
          </Segmented>

          {controller.mode === "daily" ? (
            <DatePicker
              locale={locale}
              className="report-date-control"
              label={t("ops.date")}
              value={controller.date}
              onChange={controller.setDate}
            />
          ) : (
            <MonthPicker
              locale={locale}
              className="report-date-control"
              value={controller.month}
              label={t("ops.monthly")}
              onChange={controller.setMonth}
            />
          )}
        </div>

        <div className="report-actionbar no-print">
          <ReportButton
            type="button"
            variant="outline"
            className="min-h-11 font-heading text-sm font-bold"
            disabled={!controller.report || controller.busy}
            onClick={controller.print}
          >
            <Printer aria-hidden="true" />
            {t("ops.print")}
          </ReportButton>
          <ReportButton
            type="button"
            variant="outline"
            className="min-h-11 font-heading text-sm font-bold"
            disabled={!controller.report || controller.busy}
            onClick={() => {
              void controller.download();
              showMobileFeedback("download-started");
            }}
          >
            <Download aria-hidden="true" />
            {t("ops.download")}
          </ReportButton>
        </div>

        {controller.busy ? (
          <div className="report-preview-canvas">
            <div className="report-paper-loading" role="status" aria-live="polite">
              <SkeletonLines className="report-loading-lines" />
              <span className="sr-only">{t("ops.generating")}</span>
            </div>
          </div>
        ) : controller.failed ? (
          <div className="report-state">
            <ScopedError
              locale={locale}
              title={t("ops.reportFailed")}
              body={t(controller.failureBodyKey)}
              onRetry={() => void controller.reload()}
            />
          </div>
        ) : empty ? (
          <div className="report-state">
            <ScopedEmpty
              glyph={<CalendarGlyph />}
              title={t("ops.noMovements")}
              body={t("ops.noMovementsBody")}
            />
          </div>
        ) : controller.report ? (
          <div
            className="report-paper-desk report-paper-desk--a4-preview report-preview-canvas"
            data-document-preview-canvas
          >
            <A4DocumentPreview kind="report" locale={locale}>
              <ReportDocument fitA4Preview locale={locale} report={controller.report} />
            </A4DocumentPreview>
          </div>
        ) : null}
      </div>

      <TabbarSpacer />
    </section>
  );
}

/* ------------------------------------------------------------------ */

export function MobileSettingsScreen({
  locale,
  user,
  csrf,
  onAdvanceYearActivated,
  refreshRevision,
}: {
  readonly locale: Locale;
  readonly user: AuthenticatedUserContract | null;
  readonly csrf: string | null;
  readonly onAdvanceYearActivated?: () => void;
  readonly refreshRevision?: number;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const isSystemAdministrator = user?.role === "system-administrator";
  const name = user
    ? locale === "ar"
      ? user.displayNameAr
      : user.displayNameFr
    : deploymentProductName;
  const role = isSystemAdministrator ? t("ops.roleAdmin") : t("profile.financialAdministrator");

  return (
    <section className="settings-screen" data-testid="settings-screen">
      <BlockTitle>{t("ops.account")}</BlockTitle>
      <Block strong inset className="account-details flex flex-wrap items-center gap-3">
        <span
          aria-hidden="true"
          className="grid size-10 shrink-0 place-items-center rounded-full bg-teal-100 font-heading text-sm font-bold text-teal-700"
        >
          {initialOf(name)}
        </span>
        <span className="flex flex-col gap-0.5">
          <strong className="font-heading text-[13.5px] font-bold">{name}</strong>
          <Bidi className="text-[11px] font-semibold text-muted-2">{user?.identifier ?? "—"}</Bidi>
        </span>
        <span className="ms-auto inline-flex h-6 items-center rounded-full bg-info-soft px-2.5 font-heading text-[11px] font-bold text-info">
          {role}
        </span>
      </Block>

      {user && csrf && (
        <>
          <BlockTitle>{t("settings.schoolSettings")}</BlockTitle>
          <div className="px-3">
            <SchoolSettingsPanel
              locale={locale}
              csrf={csrf}
              role={user.role}
              mobile
              {...(refreshRevision !== undefined ? { refreshRevision } : {})}
              onSaved={() => showMobileFeedback("settings-saved")}
              {...(onAdvanceYearActivated ? { onAdvanceYearActivated } : {})}
            />
          </div>
        </>
      )}
      {isSystemAdministrator && csrf && <MobileInstallationRequests locale={locale} csrf={csrf} />}
      {isSystemAdministrator && csrf && <MobileAccessCodes locale={locale} csrf={csrf} />}
      {isSystemAdministrator && <MobileSystemStatus locale={locale} />}

      <TabbarSpacer />
    </section>
  );
}

function MobileInstallationRequests({
  locale,
  csrf,
}: {
  readonly locale: Locale;
  readonly csrf: string;
}) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const requests = useInstallationRequests(csrf, {
    onResolved: (action) =>
      showMobileFeedback(
        action === "authorize"
          ? "installation-approved"
          : action === "reject"
            ? "installation-rejected"
            : "installation-revoked",
      ),
  });

  return (
    <div data-testid="installation-requests">
      <BlockTitle>{t("ops.deviceRequests")}</BlockTitle>
      {requests.loading ? (
        <Block strong inset>
          <SkeletonLines />
        </Block>
      ) : requests.failed ? (
        <Block>
          <ScopedError locale={locale} onRetry={requests.reload} />
        </Block>
      ) : requests.items.length === 0 ? (
        <Block strong inset className="text-center text-xs text-muted-foreground">
          {t("ops.deviceRequestsEmpty")}
        </Block>
      ) : (
        requests.items.map((item) => {
          const requester =
            locale === "ar" ? item.requestedBy.displayNameAr : item.requestedBy.displayNameFr;
          const reference = item.id.replaceAll("-", "").slice(0, 6).toUpperCase();
          const statusKey =
            item.status === "pending"
              ? "ops.deviceStatusPending"
              : item.status === "authorized"
                ? "ops.deviceStatusAuthorized"
                : item.status === "rejected"
                  ? "ops.deviceStatusRejected"
                  : "ops.deviceStatusRevoked";
          return (
            <Block key={item.id} strong inset className="flex flex-col gap-3">
              <div className="flex items-start gap-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-warning-soft font-heading text-xs font-bold text-warning">
                  {initialOf(requester)}
                </span>
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <strong className="font-heading text-[13px] font-bold">{requester}</strong>
                  <Bidi className="text-[11px] font-semibold text-muted-2">
                    {item.requestedBy.identifier}
                  </Bidi>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <Bidi className="text-[11px] font-bold text-muted-foreground">{reference}</Bidi>
                  <span
                    data-status={item.status}
                    className={`rounded-full px-2 py-0.5 font-heading text-[10px] font-bold ${
                      item.status === "pending"
                        ? "bg-warning-soft text-warning"
                        : item.status === "authorized"
                          ? "bg-success-soft text-success"
                          : item.status === "rejected"
                            ? "bg-[#fdf0ef] text-destructive"
                            : "bg-sunken text-muted-foreground"
                    }`}
                  >
                    {t(statusKey)}
                  </span>
                </span>
              </div>
              <div className="flex flex-col gap-1 text-[11px] text-muted-foreground">
                <span>
                  {t("ops.deviceRequestDevice")}: <Bidi>{item.label}</Bidi>
                </span>
                <span>
                  {t("ops.deviceRequestAt")}: <Bidi>{formatStamp(item.requestedAt, locale)}</Bidi>
                </span>
                {item.decidedAt && (
                  <span>
                    {t("ops.deviceDecision")}: <Bidi>{formatStamp(item.decidedAt, locale)}</Bidi>
                  </span>
                )}
              </div>
              {item.status === "pending" ? (
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    component="button"
                    type="button"
                    rounded
                    className="min-h-11"
                    disabled={Boolean(requests.busyId)}
                    onClick={() => void requests.approve(item.id)}
                  >
                    {t("ops.deviceRequestApprove")}
                  </Button>
                  <Button
                    component="button"
                    type="button"
                    rounded
                    colors={{ fill: "bg-destructive text-white active:bg-destructive/90" }}
                    className="min-h-11"
                    disabled={Boolean(requests.busyId)}
                    onClick={() => void requests.reject(item.id)}
                  >
                    {t("ops.deviceRequestReject")}
                  </Button>
                </div>
              ) : item.status === "authorized" ? (
                <Button
                  component="button"
                  type="button"
                  rounded
                  colors={{ fill: "bg-destructive text-white active:bg-destructive/90" }}
                  className="min-h-11"
                  disabled={Boolean(requests.busyId)}
                  onClick={() => {
                    if (window.confirm(t("ops.deviceRevokeConfirm"))) void requests.revoke(item.id);
                  }}
                >
                  {t("ops.deviceRequestRevoke")}
                </Button>
              ) : null}
            </Block>
          );
        })
      )}
    </div>
  );
}

function MobileAccessCodes({ locale, csrf }: { readonly locale: Locale; readonly csrf: string }) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const codes = useAccessCodes(csrf, {
    onIssued: () => showMobileFeedback("access-code-issued"),
  });

  return (
    <>
      <BlockTitle>{t("ops.accessCodes")}</BlockTitle>
      <form
        className="admin-code-card"
        onSubmit={(event) => {
          event.preventDefault();
          void codes.issue();
        }}
      >
        <BlockTitle className="!mb-1 !mt-2 text-[11px]">{t("ops.forUser")}</BlockTitle>
        <Block className="!my-1">
          <Segmented
            strong
            className="mobile-segmented"
            data-tab-count="2"
            data-active-index={codes.identifier === "FA-LOCAL-001" ? "0" : "1"}
          >
            {["FA-LOCAL-001", "SA-LOCAL-001"].map((identifier) => (
              <SegmentedButton
                key={identifier}
                active={codes.identifier === identifier}
                className="min-h-11 text-xs"
                onClick={() => codes.setIdentifier(identifier)}
              >
                {identifier}
              </SegmentedButton>
            ))}
          </Segmented>
        </Block>
        <BlockTitle className="!mb-1 !mt-2 text-[11px]">{t("ops.purpose")}</BlockTitle>
        <Block className="!my-1">
          <Segmented
            strong
            className="mobile-segmented"
            data-tab-count="2"
            data-active-index={codes.purpose === "enrollment" ? "0" : "1"}
          >
            <SegmentedButton
              active={codes.purpose === "enrollment"}
              className="min-h-11"
              onClick={() => codes.setPurpose("enrollment")}
            >
              {t("ops.purposePasskey")}
            </SegmentedButton>
            <SegmentedButton
              active={codes.purpose === "recovery"}
              className="min-h-11"
              onClick={() => codes.setPurpose("recovery")}
            >
              {t("admin.recovery")}
            </SegmentedButton>
          </Segmented>
        </Block>
        <Block>
          <Button
            component="button"
            type="submit"
            large
            rounded
            className="min-h-12"
            disabled={codes.busy}
          >
            {t("admin.issue")}
          </Button>
        </Block>
        {codes.issued && (
          <Block>
            <AccessCodeReveal
              locale={locale}
              code={codes.issued}
              expiresAt={codes.issuedExpiresAt}
              onCopied={() => showMobileFeedback("copied")}
            />
          </Block>
        )}
      </form>
    </>
  );
}

function MobileSystemStatus({ locale }: { readonly locale: Locale }) {
  const t = (key: Parameters<typeof translate>[1]) => translate(locale, key);
  const { status, failed, reload } = useSystemStatus();

  return (
    <div data-testid="status-screen">
      <BlockTitle>{t("ops.systemStatus")}</BlockTitle>
      {status ? (
        <List strong inset className="service-status-list">
          <ListItem
            title={
              <span className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className={`size-2 rounded-full ring-3 ${
                    status.state === "ready"
                      ? "bg-success ring-success/15"
                      : "bg-warning ring-warning/15"
                  }`}
                />
                {t("ops.service")}
              </span>
            }
            after={
              <Bidi className="text-[11px] text-muted-foreground">
                v{status.application.version} · {status.database.datasetVersion}
              </Bidi>
            }
          />
          <ListItem
            title={
              <span className="flex items-center gap-2.5">
                <span
                  aria-hidden="true"
                  className={`size-2 rounded-full ring-3 ${
                    status.worker.ready
                      ? "bg-success ring-success/15"
                      : "bg-warning ring-warning/15"
                  }`}
                />
                {t("ops.worker")}
              </span>
            }
            after={
              status.worker.ready ? (
                status.worker.heartbeatAt ? (
                  <Bidi className="text-[11px] text-muted-foreground">
                    {formatStamp(status.worker.heartbeatAt, locale)}
                  </Bidi>
                ) : (
                  <span className="text-[11px] text-muted-foreground">
                    {t("settings.available")}
                  </span>
                )
              ) : (
                <Button
                  component="button"
                  type="button"
                  rounded
                  outline
                  className="min-h-11"
                  onClick={reload}
                >
                  {t("ops.retry")}
                </Button>
              )
            }
          />
        </List>
      ) : failed ? (
        <Block>
          <ScopedError locale={locale} onRetry={reload} />
        </Block>
      ) : (
        <Block strong inset>
          <SkeletonLines />
        </Block>
      )}
    </div>
  );
}
